import React, { useState, useEffect } from 'react';
import { Modal, Select, Checkbox, Alert, Typography, Button, Tooltip } from 'antd';
import { DownloadOutlined, QuestionCircleOutlined, FileTextOutlined, GlobalOutlined, InfoCircleOutlined } from '@ant-design/icons';
import RecommendedTag from './components/RecommendedTag'

const { Option } = Select;
const { Text } = Typography;

interface ChapterAudioStatus {
  summary: {
    all_audio: number;
    partial_audio: number;
    no_audio: number;
    total_chapters: number;
  };
  intro: {
    status: string;
    has_audio: boolean;
    formats?: { [format: string]: string };
  };
  outro: {
    status: string;
    has_audio: boolean;
    formats?: { [format: string]: string };
  };
  full_book: {
    formats: {
      [format: string]: {
        url: string;
        timestamp?: string;
      };
    };
  };
  chapters: {
    [chapterId: string]: {
      name: string;
      status: string;
      has_audio: boolean;
      formats?: { [format: string]: string };
    };
  };
}

interface DownloadOptions {
  format: string;
  byChapter: boolean;
  fullAudio: boolean;
}

interface ExportAudiobookModalProps {
  isVisible: boolean;
  onCancel: () => void;
  onDownload: (options: DownloadOptions) => void;
  onConvert?: (options: DownloadOptions) => void;
  bookLanguage: string;
  hasCompletedChapters: boolean;
  chapterAudioStatus: ChapterAudioStatus | null;
}

const formatOptions = [
  { value: 'mp3', label: 'MP3', description: 'Most compatible audio format' },
  { value: 'm4b', label: 'M4B', description: 'Apple Audiobook format with chapter markers' },
];

const getFormatOption = (format: string) => (
  formatOptions.find(option => option.value === format) ||
  { value: format, label: format.toUpperCase(), description: 'Audio format' }
);

const hasFullAudioFormat = (
  formats: Record<string, unknown> | undefined | null,
  selectedFormat: string,
): boolean => {
  if (!formats) return false;
  const normalized = selectedFormat.toLowerCase();
  const acceptedKeys = new Set([
    `full_audio_${normalized}`,
    `full_${normalized}`,
    normalized,
  ]);
  return Object.keys(formats).some(key => acceptedKeys.has(key.toLowerCase()));
};

const ExportAudiobookModal: React.FC<ExportAudiobookModalProps> = ({
  isVisible,
  onCancel,
  onDownload,
  onConvert,
  bookLanguage,
  hasCompletedChapters,
  chapterAudioStatus
}) => {
  const [format, setFormat] = useState<string>('mp3');
  const [byChapter, setByChapter] = useState<boolean>(true);
  const [fullAudio, setFullAudio] = useState<boolean>(false);
  const [conversionNeeded, setConversionNeeded] = useState<boolean>(false);

  const getExportOptionDescriptions = (selectedFormat: string) => {
    switch (selectedFormat) {
      case 'mp3':
        return {
          byChapter: 'Zip file containing individual MP3 files for each chapter',
          fullAudio: 'Single MP3 file with all chapters combined'
        };
      case 'm4b':
        return {
          byChapter: 'Not available - M4B format is designed for complete audiobooks',
          fullAudio: 'M4B audiobook file with embedded chapter markers'
        };
      case 'lpf':
        return {
          byChapter: 'Not available - LPF packages are for complete audiobook distribution',
          fullAudio: 'LPF package optimized for Findaway/Spotify distribution'
        };
      case 'ogg':
        return {
          byChapter: 'Zip file containing individual OGG files for each chapter',
          fullAudio: 'Single OGG file with all chapters combined'
        };
      case 'aac':
        return {
          byChapter: 'Zip file containing individual AAC files for each chapter',
          fullAudio: 'Single AAC file with all chapters combined'
        };
      case 'flac':
        return {
          byChapter: 'Zip file containing individual FLAC files for each chapter',
          fullAudio: 'Single FLAC file with all chapters combined (lossless quality)'
        };
      default:
        return {
          byChapter: 'Zip file containing individual MP3 files for each chapter',
          fullAudio: 'Single MP3 file with all chapters combined'
        };
    }
  };

  const isFullAudioFormatAvailable = (selectedFormat: string): boolean => {
    return hasFullAudioFormat(chapterAudioStatus?.full_book?.formats, selectedFormat);
  }

  const isChapterZipFormatAvailable = (selectedFormat: string): boolean => {
    if (!chapterAudioStatus?.full_book?.formats) {
      return false;
    }

    return Object.keys(chapterAudioStatus.full_book.formats)
      .some(key => key === `chapters_zip_${selectedFormat}`);
  }

  const handleFormatChange = (value: string) => {
    setFormat(value);

    if (value === 'm4b' || value === 'lpf') {
      setByChapter(false);

      if (!fullAudio) {
        setFullAudio(true);
      }
    }

    const fullAudioExists = isFullAudioFormatAvailable(value);
    const chapterZipExists = isChapterZipFormatAvailable(value);

    if (fullAudioExists) {
      setFullAudio(false);
    }

    if (chapterZipExists) {
      setByChapter(false);
    }
  };

  useEffect(() => {
    const fullAudioExists = isFullAudioFormatAvailable(format);
    const chapterZipExists = isChapterZipFormatAvailable(format);

    if (fullAudioExists && fullAudio) {
      setFullAudio(false);
    }

    if (chapterZipExists && byChapter) {
      setByChapter(false);
    }
  }, [format, isVisible, chapterAudioStatus]);

  const handleByChapterChange = (e: any) => {
    setByChapter(e.target.checked);
  };

  const handleFullAudioChange = (e: any) => {
    setFullAudio(e.target.checked);
  };

  const checkIfConversionNeeded = () => {
    if (!chapterAudioStatus) {
      return true;
    }

    if (fullAudio && !isFullAudioFormatAvailable(format)) {
      return true;
    }

    if (byChapter && !isChapterZipFormatAvailable(format)) {
      return true;
    }

    return false;
  }

  useEffect(() => {
    setConversionNeeded(checkIfConversionNeeded());
  }, [format, byChapter, fullAudio, chapterAudioStatus]);

  const handleAction = () => {
    if (!byChapter && !fullAudio) {
      return;
    }

    const options = {
      format,
      byChapter,
      fullAudio
    };

    if (conversionNeeded && onConvert) {
      onConvert(options);
    } else {
      onDownload(options);
    }
  };

  const isFullAudioDisabled = isFullAudioFormatAvailable(format);
  const isChapterZipDisabled = isChapterZipFormatAvailable(format) || format === 'm4b' || format === 'lpf';
  const descriptions = getExportOptionDescriptions(format);
  const selectedFormatOption = getFormatOption(format);
  const selectedOutputLabels = [
    byChapter ? 'chapter zip' : null,
    fullAudio ? 'full audiobook' : null
  ].filter(Boolean);
  const allSelectedFormatFilesExist = isChapterZipDisabled && isFullAudioDisabled;
  const blockerMessage = !hasCompletedChapters
    ? 'No completed chapters are ready to export.'
    : !byChapter && !fullAudio
      ? allSelectedFormatFilesExist
        ? 'All files for this format already exist. Choose a different format to generate new files.'
        : 'Please select at least one export option.'
      : null;
  const actionDisabled = !hasCompletedChapters || (!byChapter && !fullAudio);
  const readinessToneClass = blockerMessage
    ? 'translation-readiness-support-note--blocking'
    : conversionNeeded
      ? 'translation-readiness-support-note--warning'
      : 'translation-readiness-support-note--success';
  const footerSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite" data-testid="tutorial:audiobook:export-footer">
      {blockerMessage ? (
        <span className="translation-modal-footer-summary__warning">{blockerMessage}</span>
      ) : (
        <>
          <span>
            <strong>{selectedOutputLabels.join(' and ')}</strong> selected
          </span>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span>
            <strong>{selectedFormatOption.label}</strong> format
          </span>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span>{conversionNeeded ? 'Preparation needed' : 'Files already available'}</span>
        </>
      )}
    </div>
  );

  return (
    <Modal
      title={<div style={{ textAlign: 'center', width: '100%' }}>Prepare Full Audiobook Files</div>}
      open={isVisible}
      onCancel={onCancel}
      centered
      className="translation-modal-footer-stable"
      width="min(820px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(76vh, calc(100vh - 220px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 16px 12px',
      }}
      footer={[
        <React.Fragment key="export-summary">
          {footerSummary}
        </React.Fragment>,
        <Button key="cancel" onClick={onCancel} data-testid="tutorial:audiobook:export-cancel">
          Cancel
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleAction}
          disabled={actionDisabled}
          data-testid="tutorial:audiobook:export-action"
        >
          Export
        </Button>,
      ]}
    >
      <div className="translation-modal-body" data-testid="tutorial:audiobook:export-modal">
        {!hasCompletedChapters && (
          <Alert
            message="No Completed Chapters"
            description="There are no completed chapters to export. Please generate audio first."
            type="error"
            showIcon
            className="modal-alert-compact"
          />
        )}

        <section className="translation-modal-block translation-modal-block--scope" aria-labelledby="export-audiobook-scope-heading" data-testid="tutorial:audiobook:export-package">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="export-audiobook-scope-heading">Audiobook package</h3>
              <p>Prepare distribution-friendly files from completed audiobook chapters.</p>
            </div>
          </div>
          <div className="translation-scope-summary">
            <div>
              <span className="translation-modal-kicker">Language</span>
              <Text strong><GlobalOutlined /> {bookLanguage || 'Not set'}</Text>
            </div>
            <div>
              <span className="translation-modal-kicker">Selected format</span>
              <Text strong>{selectedFormatOption.label}</Text>
            </div>
          </div>
        </section>

        <section className="translation-modal-block" aria-labelledby="export-audiobook-format-heading" data-testid="tutorial:audiobook:export-format">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="export-audiobook-format-heading"><FileTextOutlined /> File format</h3>
              <p>Select the package format you want to prepare or download.</p>
            </div>
          </div>
          <Select
            style={{ width: '100%' }}
            value={format}
            onChange={handleFormatChange}
            aria-label="Audiobook export format"
          >
            {formatOptions.map(option => (
              <Option key={option.value} value={option.value}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <span>
                    <Text strong>{option.label}</Text>
                    {option.value === 'mp3' && <RecommendedTag />}
                  </span>
                  <Text type="secondary">{option.description}</Text>
                </div>
              </Option>
            ))}
          </Select>
        </section>

        <section className="translation-modal-block" aria-labelledby="export-audiobook-options-heading" data-testid="tutorial:audiobook:export-options">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="export-audiobook-options-heading"><DownloadOutlined /> Export options</h3>
              <p>Select one or both outputs. Already available files are shown as unavailable for duplicate preparation.</p>
            </div>
            <Tooltip title="Select at least one export option">
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div className="translation-quality-grid audiobook-modal-option-grid">
                <Tooltip title={
                  isChapterZipDisabled
                    ? (format === 'm4b' || format === 'lpf')
                      ? `${format.toUpperCase()} format is only available for full audiobooks`
                      : 'Chapter files already exist in this format'
                    : ''
                }>
                  <div className={`translation-quality-control audiobook-modal-option-card${byChapter ? ' audiobook-modal-option-card--selected' : ''}`}>
                    <Checkbox
                      checked={byChapter}
                      onChange={handleByChapterChange}
                      disabled={isChapterZipDisabled}
                    >
                      <Text>Separate files by chapter</Text>
                    </Checkbox>
                    <div className="translation-quality-helper">{descriptions.byChapter}</div>
                    {isChapterZipDisabled && isChapterZipFormatAvailable(format) ? (
                      <div className="translation-readiness-support-note translation-readiness-support-note--success">
                        Already Available
                      </div>
                    ) : isChapterZipDisabled ? (
                      <div className="translation-readiness-support-note translation-readiness-support-note--warning">
                        {selectedFormatOption.label} exports use a full-audiobook file.
                      </div>
                    ) : null}
                  </div>
                </Tooltip>

                <Tooltip title={isFullAudioDisabled ? 'Full audiobook already exists in this format' : ''}>
                  <div className={`translation-quality-control audiobook-modal-option-card${fullAudio ? ' audiobook-modal-option-card--selected' : ''}`}>
                    <Checkbox
                      checked={fullAudio}
                      onChange={handleFullAudioChange}
                      disabled={isFullAudioDisabled}
                    >
                      <Text>One file for the full audiobook</Text>
                    </Checkbox>
                    <div className="translation-quality-helper">{descriptions.fullAudio}</div>
                    {isFullAudioDisabled ? (
                      <div className="translation-readiness-support-note translation-readiness-support-note--success">
                        Already Available
                      </div>
                    ) : null}
                  </div>
                </Tooltip>
          </div>
        </section>

        <section className="translation-modal-block" aria-labelledby="export-audiobook-readiness-heading" data-testid="tutorial:audiobook:export-readiness">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="export-audiobook-readiness-heading">Readiness</h3>
              <p>Review blockers before starting export preparation.</p>
            </div>
          </div>
          <div className="translation-readiness-panel audiobook-modal-status-panel">
            <div className="translation-modal-subheading">Export status</div>
            <div className={`translation-readiness-support-note ${readinessToneClass}`}>
              {blockerMessage || (conversionNeeded
                ? `Ready to prepare ${selectedOutputLabels.join(' and ')} as ${selectedFormatOption.label}.`
                : `Ready to export existing ${selectedOutputLabels.join(' and ')} as ${selectedFormatOption.label}.`)}
            </div>
            <div className="translation-readiness-support-note">
              <InfoCircleOutlined /> Only completed chapters will be included in the files.
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default ExportAudiobookModal;
